// Scroll behaviour BrowserRouter does not provide on its own:
//  - a link to "/#pricing" (from another page, or re-clicked on the landing
//    page) scrolls to that section once it has rendered;
//  - any other forward navigation to a NEW PATH starts at the top instead of
//    keeping the previous page's scroll offset (search-param-only changes,
//    e.g. a date stepper, keep their position).
// Back/forward (POP) is left alone so the browser can restore its position.
import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/** The element id a location hash points at ("" when none). */
export function hashTarget(hash: string): string {
  if (!hash || hash === "#") return "";
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(pathname);

  useEffect(() => {
    const pathChanged = prevPath.current !== pathname;
    prevPath.current = pathname;
    const id = hashTarget(hash);
    if (id) {
      // The target may live in a lazily rendered section: retry for a short
      // while (about one second) until it exists.
      let tries = 0;
      let raf = 0;
      const attempt = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (++tries < 60) raf = window.requestAnimationFrame(attempt);
      };
      attempt();
      return () => window.cancelAnimationFrame(raf);
    }
    if (pathChanged && navType !== "POP") window.scrollTo(0, 0);
    return undefined;
    // `key` changes on every navigation, so re-clicking "#pricing" while
    // already on it scrolls again.
  }, [pathname, hash, key, navType]);

  return null;
}
